import React from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { cardImageUrl, imageUrl } from '../services/helpers';
import { Marker, TrainerCutOut, TrainerIcon } from './styled';

/** A skull, white on the poison marker's purple: the classic look of the game's poison markers. */
const Skull = () => (
  <svg viewBox="0 0 24 24" width="70%" height="70%" aria-hidden="true">
    <path
      fill="#fff"
      d="M12 2.5c-4.7 0-8.3 3.4-8.3 7.8 0 2.6 1.2 4.6 3 5.8v2.4c0 .8.6 1.4 1.4 1.4h.9v1.6h1.6v-1.6h2.8v1.6h1.6v-1.6h.9c.8 0 1.4-.6 1.4-1.4v-2.4c1.8-1.2 3-3.2 3-5.8 0-4.4-3.6-7.8-8.3-7.8z"
    />
    <circle cx="8.6" cy="11" r="2.1" fill="#4b1c74" />
    <circle cx="15.4" cy="11" r="2.1" fill="#4b1c74" />
    <path fill="#4b1c74" d="M12 13.4l-1.3 2.4h2.6z" />
  </svg>
);

/** A flame, for the burn marker. */
const Flame = () => (
  <svg viewBox="0 0 24 24" width="66%" height="66%" aria-hidden="true">
    <path fill="#fff" d="M12.4 2c.6 3.2-1.5 4.7-3 6.6-1.4 1.8-2.6 3.6-2.1 6.2.6 3.2 3.2 5.2 6 5.2 3.4 0 5.9-2.6 5.9-6 0-2.7-1.4-4.4-2.6-5.9-.3 1.5-1 2.5-2.1 3 .5-3.3-.4-6.6-2.1-9.1z" />
  </svg>
);

const MARKERS = {
  poison: { label: 'Poisoned', art: <Skull />, color: '#6b2ca3' },
  'bad-poison': { label: 'Badly Poisoned', art: <Skull />, color: '#4b1c74', count: 2 },
  burn: { label: 'Burned', art: <Flame />, color: '#d9541e' },
};

/** A marker put on a Pokémon for a Special Condition, like the poison marker (conditionDisplay says which). */
export const ConditionMarker = ({ kind }) => {
  const marker = MARKERS[kind];
  return (
    <Marker $color={marker.color} title={marker.label} aria-label={marker.label} role="img">
      {marker.art}
      {marker.count && <b>{marker.count}</b>}
    </Marker>
  );
};

ConditionMarker.propTypes = {
  kind: PropTypes.oneOf(Object.keys(MARKERS)).isRequired,
};

/**
 * The item cut out of an attachable Trainer's artwork, by card name (public/image/attached on the server): the
 * capsule itself, background removed. Made from the Base Set cards; the same Trainers in later sets share the art.
 */
const CUT_OUTS = {
  PlusPower: '/attached/pluspower.png',
  Defender: '/attached/defender.png',
};

/**
 * A Trainer attached to a Pokémon, like PlusPower, as a little icon the size of the energy symbols beside it: its item
 * cut out of the card's artwork, or for a Trainer with no cut-out yet, a round crop of the artwork.
 */
export const AttachedTrainer = ({ card }) => {
  const name = card.get('name');
  const cutOut = CUT_OUTS[name];

  return cutOut
    ? <TrainerCutOut src={imageUrl(cutOut)} alt={name} title={name} />
    : <TrainerIcon style={{ backgroundImage: `url(${cardImageUrl(card)})` }} title={name} aria-label={name} role="img" />;
};

AttachedTrainer.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
};
